import { MarketPotential } from 'apis/CompanyAPI/companies/useMarketPotential';
import React, { useRef, useState } from 'react';
import { CompanyProfile } from 'types/company';
import SectionHeading from 'ui/elements/SectionHeading';
import EditAction from 'ui/elements/actions/EditAction';
import { BODY_ID } from 'ui/views/layouts/BaseLayout/ScrollableBody';
import { useDebouncedCallback } from 'use-debounce';
import { IResource, getOrUndefined } from 'util/resource';
import MarketPotentialDialog from './MarketPotentialDialog';
import { useAiOnboardingSessionStorage } from 'domain/onboarding/company/Wizard/AIOnboarding';
import AIChip from '../AiChip';
import MarketPotentialCard from './MarketPotentialCard';

interface Props {
  company: CompanyProfile;
  marketPotentialResource?: IResource<MarketPotential>;
  reloadMarketPotential: () => void;
  canEditCompany: boolean;
}

export const hasMarketPotential = (marketPotential?: MarketPotential) =>
  marketPotential?.currentMarketFocus ||
  marketPotential?.geographicalMarketPotential ||
  marketPotential?.description ||
  marketPotential?.estimatedAvailableMarket?.value ||
  marketPotential?.estimatedServiceableMarket?.value;

type Field =
  | 'geographical-potential'
  | 'current-focus'
  | 'estimated-available'
  | 'estimated-serviceable'
  | 'description';

export default function MarketPotentialSection({
  company,
  marketPotentialResource,
  reloadMarketPotential,
  canEditCompany,
}: Props) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  // Decide which fields should be visible when dialog is triggered from the inline elements
  const [dialogTriggerField, setDialogTriggerField] = useState<Field>();

  // We need to add a delayed reset when we close the dialog.
  // Otherwise, we will se the full form in a flash before the dialog is closed when we only
  // have shown one visible field.
  const delayedResetField = useDebouncedCallback(() => setDialogTriggerField(undefined), 100);
  const marketPotential = getOrUndefined(marketPotentialResource);

  const [isAiOnboarding] = useAiOnboardingSessionStorage();
  if (!canEditCompany && !hasMarketPotential(marketPotential)) {
    return null;
  }

  const openDialog = (field: Field) => {
    setDialogTriggerField(field);
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    delayedResetField();

    // Scroll to section if dialog has caused scrolling and the section getting out of view
    const sectionTop = sectionRef.current?.offsetTop;
    const body = document.getElementById(BODY_ID);
    body?.scrollTo({ top: sectionTop && sectionTop < window.scrollY ? sectionTop - 20 : window.scrollY });
  };

  const hasAnyTopGridAttributes =
    canEditCompany ||
    !!marketPotential?.estimatedAvailableMarket?.value ||
    !!marketPotential?.estimatedServiceableMarket?.value ||
    (marketPotential?.currentMarketFocus ?? []).length > 0 ||
    (marketPotential?.geographicalMarketPotential ?? []).length > 0;

  return (
    <div ref={sectionRef}>
      <SectionHeading
        heading={
          <>
            Market potential
            {isAiOnboarding && hasAnyTopGridAttributes && (
              <AIChip className="u-half-spacing-left" onClick={() => setIsDialogOpen(true)} />
            )}
          </>
        }
        data-intercom-target={canEditCompany ? 'company-overview-market-potential-heading' : undefined}
        addMobileMargin
      >
        {canEditCompany && (
          <button onClick={() => setIsDialogOpen(true)} aria-label="Edit section">
            <EditAction tooltip="Edit section" />
          </button>
        )}
      </SectionHeading>
      <MarketPotentialCard
        canEditCompany={canEditCompany}
        marketPotential={marketPotential}
        onClickAddField={openDialog}
      />
      <MarketPotentialDialog
        company={company}
        marketPotential={marketPotential}
        isOpen={isDialogOpen}
        onClose={closeDialog}
        autoFocusField={dialogTriggerField}
        onComplete={() => reloadMarketPotential()}
      />
    </div>
  );
}
